import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface CreateTaskDto {
  title: string;
  description?: string;
  createdById: string;
  assigneeIds?: string[];
  dueDate?: Date | string;
  priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  projectId?: string;
  parentTaskId?: string;
  tags?: string[];
  estimatedHours?: number;
}

export interface UpdateTaskStatusDto {
  status: string;
  userId: string;
}

export interface AssignTaskDto {
  userId: string;
  assignedBy: string;
  role?: string;
}

export interface AddCommentDto {
  userId: string;
  content: string;
  parentId?: string;
}

export interface AddDependencyDto {
  dependsOnTaskId: string;
  type?: string;
}

export interface StartTimeTrackingDto {
  userId: string;
  description?: string;
}

export interface BulkUpdatePositionsDto {
  updates: Array<{ taskId: string; position: number; status: string }>;
}

export interface Task {
  id: string;
  title: string;
  description?: string;
  status: string;
  priority: string;
  createdById: string;
  projectId?: string;
  parentTaskId?: string;
  dueDate?: Date;
  estimatedHours?: number;
  tags?: string[];
  createdAt: Date;
  updatedAt: Date;
}

export interface KanbanBoard {
  todo: Task[];
  inProgress: Task[];
  review: Task[];
  done: Task[];
}

export interface TaskMetrics {
  total: number;
  byStatus: Record<string, number>;
  byPriority: Record<string, number>;
  overdue: number;
  completed: number;
}

export interface UserDashboard {
  myTasks: Task[];
  assignedTasks: Task[];
  overdueTasks: Task[];
  recentActivity: any[];
  statistics: any;
}

@Injectable({ providedIn: 'root' })
export class TaskService {
  private readonly baseUrl = `${environment.apiGatewayUrl}/api/v1/tasks`;

  constructor(private http: HttpClient) {}

  // ============ BASIC CRUD ============
  createTask(dto: CreateTaskDto): Observable<{ success: boolean; message?: string; data: Task }> {
    return this.http.post<{ success: boolean; message?: string; data: Task }>(this.baseUrl, dto);
  }

  getTask(id: string): Observable<{ success: boolean; data: Task }> {
    return this.http.get<{ success: boolean; data: Task }>(`${this.baseUrl}/${id}`);
  }

  // ============ USER TASKS ============
  getUserTasks(
    userId: string,
    status?: string,
    limit: number = 50,
    offset: number = 0
  ): Observable<{ success: boolean; data: Task[] }> {
    let params = new HttpParams()
      .set('limit', limit.toString())
      .set('offset', offset.toString());
    if (status) {
      params = params.set('status', status);
    }
    return this.http.get<{ success: boolean; data: Task[] }>(`${this.baseUrl}/user/${userId}`, { params });
  }

  // ============ STATUS UPDATES ============
  updateTaskStatus(
    taskId: string,
    dto: UpdateTaskStatusDto
  ): Observable<{ success: boolean; message?: string; data: Task }> {
    return this.http.patch<{ success: boolean; message?: string; data: Task }>(
      `${this.baseUrl}/${taskId}/status`,
      dto
    );
  }

  // ============ TASK ASSIGNMENT ============
  assignTask(
    taskId: string,
    dto: AssignTaskDto
  ): Observable<{ success: boolean; message?: string; data: any }> {
    return this.http.post<{ success: boolean; message?: string; data: any }>(
      `${this.baseUrl}/${taskId}/assign`,
      dto
    );
  }

  // ============ KANBAN BOARD ============
  getKanbanBoard(
    projectId?: string,
    userId?: string
  ): Observable<{ success: boolean; data: KanbanBoard }> {
    let params = new HttpParams();
    if (projectId) params = params.set('projectId', projectId);
    if (userId) params = params.set('userId', userId);
    return this.http.get<{ success: boolean; data: KanbanBoard }>(`${this.baseUrl}/kanban/board`, { params });
  }

  bulkUpdatePositions(
    dto: BulkUpdatePositionsDto
  ): Observable<{ success: boolean; message?: string; data: any }> {
    return this.http.patch<{ success: boolean; message?: string; data: any }>(
      `${this.baseUrl}/bulk/positions`,
      dto
    );
  }

  // ============ COMMENTS ============
  addComment(
    taskId: string,
    dto: AddCommentDto
  ): Observable<{ success: boolean; message?: string; data: any }> {
    return this.http.post<{ success: boolean; message?: string; data: any }>(
      `${this.baseUrl}/${taskId}/comments`,
      dto
    );
  }

  getRecentComments(limit: number = 10): Observable<{ success: boolean; data: any[] }> {
    const params = new HttpParams().set('limit', limit.toString());
    return this.http.get<{ success: boolean; data: any[] }>(`${this.baseUrl}/comments/recent`, { params });
  }

  // ============ TIMELINE ============
  getTaskTimeline(taskId: string): Observable<{ success: boolean; data: any[] }> {
    return this.http.get<{ success: boolean; data: any[] }>(`${this.baseUrl}/${taskId}/timeline`);
  }

  // ============ WATCHERS ============
  toggleWatcher(taskId: string, userId: string): Observable<{ success: boolean; data: any }> {
    return this.http.post<{ success: boolean; data: any }>(`${this.baseUrl}/${taskId}/watch`, { userId });
  }

  // ============ DEPENDENCIES ============
  addDependency(
    taskId: string,
    dto: AddDependencyDto
  ): Observable<{ success: boolean; message?: string; data: any }> {
    return this.http.post<{ success: boolean; message?: string; data: any }>(
      `${this.baseUrl}/${taskId}/dependencies`,
      dto
    );
  }

  getDependencies(taskId: string): Observable<{ success: boolean; data: any[] }> {
    return this.http.get<{ success: boolean; data: any[] }>(`${this.baseUrl}/${taskId}/dependencies`);
  }

  removeDependency(taskId: string, dependencyId: string): Observable<{ success: boolean; message?: string }> {
    return this.http.delete<{ success: boolean; message?: string }>(
      `${this.baseUrl}/${taskId}/dependencies/${dependencyId}`
    );
  }

  // ============ SUBTASKS ============
  addSubtask(taskId: string, data: any): Observable<{ success: boolean; data: any }> {
    return this.http.post<{ success: boolean; data: any }>(`${this.baseUrl}/${taskId}/subtasks`, data);
  }

  updateSubtask(
    taskId: string,
    subtaskId: string,
    data: any
  ): Observable<{ success: boolean; data: any }> {
    return this.http.patch<{ success: boolean; data: any }>(
      `${this.baseUrl}/${taskId}/subtasks/${subtaskId}`,
      data
    );
  }

  toggleSubtask(taskId: string, subtaskId: string): Observable<{ success: boolean; data: any }> {
    return this.http.post<{ success: boolean; data: any }>(
      `${this.baseUrl}/${taskId}/subtasks/${subtaskId}/toggle`,
      {}
    );
  }

  deleteSubtask(taskId: string, subtaskId: string): Observable<{ success: boolean; message?: string }> {
    return this.http.delete<{ success: boolean; message?: string }>(
      `${this.baseUrl}/${taskId}/subtasks/${subtaskId}`
    );
  }

  // ============ ATTACHMENTS ============
  uploadAttachment(taskId: string, data: any): Observable<{ success: boolean; data: any }> {
    return this.http.post<{ success: boolean; data: any }>(`${this.baseUrl}/${taskId}/attachments`, data);
  }

  deleteAttachment(
    taskId: string,
    attachmentId: string
  ): Observable<{ success: boolean; message?: string }> {
    return this.http.delete<{ success: boolean; message?: string }>(
      `${this.baseUrl}/${taskId}/attachments/${attachmentId}`
    );
  }

  // ============ HISTORY ============
  getTaskHistory(taskId: string): Observable<{ success: boolean; data: any[] }> {
    return this.http.get<{ success: boolean; data: any[] }>(`${this.baseUrl}/${taskId}/history`);
  }

  // ============ TEMPLATES ============
  getAllTemplates(): Observable<{ success: boolean; data: any[] }> {
    return this.http.get<{ success: boolean; data: any[] }>(`${this.baseUrl}/templates`);
  }

  getTemplate(id: string): Observable<{ success: boolean; data: any }> {
    return this.http.get<{ success: boolean; data: any }>(`${this.baseUrl}/templates/${id}`);
  }

  createTemplate(data: any): Observable<{ success: boolean; data: any }> {
    return this.http.post<{ success: boolean; data: any }>(`${this.baseUrl}/templates`, data);
  }

  createTaskFromTemplate(templateId: string, data: any): Observable<{ success: boolean; data: Task }> {
    return this.http.post<{ success: boolean; data: Task }>(
      `${this.baseUrl}/templates/${templateId}/create-task`,
      data
    );
  }

  deleteTemplate(id: string): Observable<{ success: boolean; message?: string }> {
    return this.http.delete<{ success: boolean; message?: string }>(`${this.baseUrl}/templates/${id}`);
  }

  saveAsTemplate(taskId: string, templateName: string): Observable<{ success: boolean; data: any }> {
    return this.http.post<{ success: boolean; data: any }>(
      `${this.baseUrl}/${taskId}/save-as-template`,
      { templateName }
    );
  }

  // ============ TIME TRACKING ============
  startTimeTracking(
    taskId: string,
    dto: StartTimeTrackingDto
  ): Observable<{ success: boolean; message?: string; data: any }> {
    return this.http.post<{ success: boolean; message?: string; data: any }>(
      `${this.baseUrl}/${taskId}/time-tracking/start`,
      dto
    );
  }

  stopTimeTracking(timeLogId: string): Observable<{ success: boolean; message?: string; data: any }> {
    return this.http.post<{ success: boolean; message?: string; data: any }>(
      `${this.baseUrl}/time-tracking/${timeLogId}/stop`,
      {}
    );
  }

  // ============ METRICS & ANALYTICS ============
  getMetrics(
    projectId?: string,
    startDate?: Date | string,
    endDate?: Date | string
  ): Observable<{ success: boolean; data: TaskMetrics }> {
    let params = new HttpParams();
    if (projectId) params = params.set('projectId', projectId);
    if (startDate) params = params.set('startDate', startDate.toString());
    if (endDate) params = params.set('endDate', endDate.toString());
    return this.http.get<{ success: boolean; data: TaskMetrics }>(`${this.baseUrl}/metrics/analytics`, {
      params,
    });
  }

  getOverdueTasks(): Observable<{ success: boolean; data: Task[] }> {
    return this.http.get<{ success: boolean; data: Task[] }>(`${this.baseUrl}/reports/overdue`);
  }

  getUserDashboard(userId: string): Observable<{ success: boolean; data: UserDashboard }> {
    return this.http.get<{ success: boolean; data: UserDashboard }>(
      `${this.baseUrl}/dashboard/${userId}`
    );
  }

  searchTasks(
    query: string,
    userId?: string,
    status?: string,
    priority?: string,
    limit: number = 50
  ): Observable<{ success: boolean; data: Task[] }> {
    let params = new HttpParams().set('q', query).set('limit', limit.toString());
    if (userId) params = params.set('userId', userId);
    if (status) params = params.set('status', status);
    if (priority) params = params.set('priority', priority);
    return this.http.get<{ success: boolean; data: Task[] }>(`${this.baseUrl}/search/query`, { params });
  }
}

